import { fileLabel, formatBytes, rejectionFor } from '@concord/domain';
import {
  Button,
  DropZone,
  FreezeNote,
  QCard,
  UploadItem,
  UploadList,
  type UploadState,
  WizardNav,
} from '@concord/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { intakeKey, uploadRecord } from '../api';
import type { StepProps } from '../IntakeWizard';

interface Local {
  key: string;
  name: string;
  ext: string;
  state: UploadState;
  abort?: AbortController;
}

const TYPE_WORDS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
};

/** DOC-01 to DOC-04: rules first, progress per file, required descriptions, and when files freeze. */
export function RecordsStep({ v, back, next, mode }: StepProps) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<Local[]>([]);
  const seq = useRef(0);
  const refresh = () => qc.invalidateQueries({ queryKey: intakeKey(v.id) });
  const update = (key: string, patch: Partial<Local>) =>
    setLocal((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  function add(files: File[]) {
    for (const file of files) {
      seq.current += 1;
      const key = `l${seq.current}`;
      const ext = fileLabel(file.name, file.type);
      const rejection = rejectionFor(file.name, file.size, file.type, {
        maxBytes: v.rules.uploadMaxBytes,
        types: v.rules.uploadTypes,
      });
      if (rejection) {
        setLocal((xs) => [
          ...xs,
          { key, name: file.name, ext, state: { kind: 'error', message: rejection } },
        ]);
        continue;
      }
      const abort = new AbortController();
      setLocal((xs) => [
        ...xs,
        { key, name: file.name, ext, abort, state: { kind: 'uploading', loaded: 0, total: file.size } },
      ]);
      uploadRecord(
        v.id,
        file,
        (loaded, total) => update(key, { state: { kind: 'uploading', loaded, total } }),
        abort.signal,
      )
        .then(async () => {
          await refresh();
          setLocal((xs) => xs.filter((x) => x.key !== key));
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') {
            setLocal((xs) => xs.filter((x) => x.key !== key));
          } else {
            update(key, {
              state: { kind: 'error', message: e instanceof Error ? e.message : errorMessage(e) },
            });
          }
        });
    }
  }

  const uploaded = v.attachments;
  const uploading = local.filter((l) => l.state.kind === 'uploading').length;
  const needs = uploaded.filter((a) => a.uploaded && !a.description).length;
  const ready = uploaded.filter((a) => a.uploaded && a.description).length;
  const status = [
    `${ready} ${ready === 1 ? 'file' : 'files'} ready`,
    uploading ? `${uploading} uploading` : null,
    needs ? `${needs} ${needs === 1 ? 'needs' : 'need'} a description` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const blocked = uploading > 0 || needs > 0;
  const types = v.rules.uploadTypes.map((t) => TYPE_WORDS[t] ?? t);
  const typeList = `${types.slice(0, -1).join(', ')}${types.length > 1 ? ' or ' : ''}${types.at(-1)}`;

  return (
    <>
      <QCard
        label="Upload records"
        title={mode === 'staff' ? 'Add the patient’s medical records' : 'Add your medical records'}
        lead="Each file needs a short description so the physicians know what they are looking at. You can add more later, up to the moment the case is sent."
      >
        <DropZone
          accept={v.rules.uploadTypes.join(',')}
          onFiles={add}
          rules={
            <>
              <b>{typeList}</b> · up to <b>{formatBytes(v.rules.uploadMaxBytes)}</b> each · photos of paper
              reports are fine
            </>
          }
        />
        <UploadList>
          {uploaded.map((a) => (
            <UploadItem
              key={a.id}
              ext={fileLabel(a.filename, a.contentType)}
              name={a.filename}
              description={a.description ?? ''}
              state={
                !a.uploaded
                  ? { kind: 'uploading', loaded: 0, total: a.sizeBytes }
                  : a.description
                    ? { kind: 'ready', sizeLabel: formatBytes(a.sizeBytes) }
                    : { kind: 'needs', sizeLabel: formatBytes(a.sizeBytes) }
              }
              onDescribe={async (description) => {
                await api(`/attachments/${a.id}`, { method: 'PATCH', body: { description } });
                await refresh();
              }}
              onDelete={async () => {
                await api(`/attachments/${a.id}`, { method: 'DELETE' });
                await refresh();
              }}
            />
          ))}
          {local.map((l) => (
            <UploadItem
              key={l.key}
              ext={l.ext}
              name={l.name}
              description=""
              state={l.state}
              onDescribe={() => undefined}
              onDelete={() => {
                l.abort?.abort();
                setLocal((xs) => xs.filter((x) => x.key !== l.key));
              }}
            />
          ))}
        </UploadList>
        <FreezeNote>
          <b>
            {mode === 'staff' ? 'These can be edited or deleted' : 'You can edit or delete these'} until the
            case is sent to the physicians.
          </b>{' '}
          After that they are fixed, because the physicians will have read them. If something is missing
          later, send it to your care navigator, who will attach it and note where it came from.
        </FreezeNote>
      </QCard>
      <WizardNav
        back={
          <Button variant="sec" onClick={back}>
            Back to history
          </Button>
        }
        status={status}
        next={
          <Button size="lg" aria-disabled={blocked} disabled={blocked} onClick={next}>
            Continue to times {mode === 'staff' ? 'they are' : "you're"} free
          </Button>
        }
      />
    </>
  );
}
